
import { useEffect, useState } from 'react';

interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: {
    icon: string;
    title: string;
    description: string;
    gradient: string;
    details: string[];
    benefits: string[];
    image: string;
  } | null;
}

export function ServiceModal({ isOpen, onClose, service }: ServiceModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 200);
  };

  if (!isOpen || !service) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      
      <div 
        className={`relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transition-all duration-200 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          <div className="relative h-64 md:h-auto md:min-h-[500px]">
            <img 
              src={service.image} 
              alt={service.title}
              className="absolute inset-0 w-full h-full object-cover object-top rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none"
            />
            <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-20 rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none`}></div>
          </div>

          <div className="p-8 md:p-10">
            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6`}>
              <i className={`${service.icon} text-3xl text-white`}></i>
            </div>

            <h3 className="font-display text-3xl font-bold text-secondary mb-4">
              {service.title}
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6">
              {service.description}
            </p>

            <div className="mb-6">
              <h4 className="font-display text-lg font-semibold text-secondary mb-3">What&apos;s Included:</h4>
              <ul className="space-y-2">
                {service.details.map((detail, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span className="text-gray-600 text-sm">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-8">
              <h4 className="font-display text-lg font-semibold text-secondary mb-3">Key Benefits:</h4>
              <ul className="space-y-2">
                {service.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-star-fill text-amber-400 mt-0.5"></i>
                    <span className="text-gray-600 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a 
                href="#contact"
                onClick={handleClose}
                className="bg-gradient-to-r from-primary to-teal-400 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-primary/30 transition-all text-center cursor-pointer whitespace-nowrap"
              >
                Get Started
              </a>
              <a 
                href="#contact"
                onClick={handleClose}
                className="border-2 border-secondary text-secondary px-6 py-3 rounded-lg font-semibold hover:bg-secondary hover:text-white transition-all text-center cursor-pointer whitespace-nowrap"
              >
                Book a Demo
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
